import { createContext, useContext } from 'react';

/**
 * Display names for game concepts that a manager can customize (Pro and up).
 * Stored on the game doc as:
 *   terms: { currency: { singular, plural }, team: { singular, plural } }
 *
 * Only the words people read change. Data fields, access keys, routes, and
 * class names keep using "currency" and "teams".
 *
 * Each term is an object so fields like an icon can be added later without
 * reshaping stored data. Keep DEFAULT_TERMS in step with gameTerms.cjs in
 * the backend.
 */
export const DEFAULT_TERMS = {
  currency: { singular: 'Coin', plural: 'Coins' },
  team: { singular: 'Team', plural: 'Teams' },
};

export const TERM_KEYS = Object.keys(DEFAULT_TERMS);
export const MAX_TERM_LENGTH = 20;

function buildTerm(custom, fallback) {
  const term = custom?.singular ? custom : fallback;
  const plural = term.plural || `${term.singular}s`;
  return {
    singular: term.singular,
    plural: plural,
    singularLower: term.singular.toLowerCase(),
    pluralLower: plural.toLowerCase(),
  };
}

/**
 * Resolves a game's terms, falling back to the defaults.
 *
 *   const terms = getGameTerms(gameData);
 *   terms.currency.plural        // "Coins" or "Tokens"
 *   terms.team.singularLower     // "team" or "guild"
 *   terms.formatCurrency(25)     // "25 Coins"
 *
 * @param {object} gameData the game doc (or null for defaults)
 * @returns {object} resolved terms
 */
export function getGameTerms(gameData) {
  const custom = gameData?.terms ?? {};
  const terms = {};
  for (const key of TERM_KEYS) {
    terms[key] = buildTerm(custom[key], DEFAULT_TERMS[key]);
  }
  terms.formatCurrency = (amount, options = {}) => {
    const num = Number(amount) || 0;
    const word = Math.abs(num) === 1
      ? terms.currency.singular
      : terms.currency.plural;
    return `${num.toLocaleString()} ${options.lower ? word.toLowerCase() : word}`;
  };
  return terms;
}

const defaultGameTerms = getGameTerms(null);

export const GameTermsContext = createContext(defaultGameTerms);

/**
 * Terms for the game being viewed. Reads from the nearest GameTermsProvider;
 * outside of one, returns the defaults.
 * @returns {object} resolved terms (see getGameTerms)
 */
export function useGameTerms() {
  return useContext(GameTermsContext);
}
